import { UserAction, useUserActionsHooks } from '@api/userActionsApiHandler';
import Card from '@components/Atoms/Card';
import DefaultButton from '@components/Atoms/DefaultButton';
import Img from '@components/Atoms/Img';
import StaticDatePickerX from '@components/Atoms/StaticDatePicker';
import Checkbox from '@components/Checkbox';
import Modal from '@components/Modal';
import useWindowDimensions from '@hooks/useWindowDimensions';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import { showToast } from '@shared/atoms';
import { SystemButton } from '@shared/molecules';
import { ToastMessages } from 'constants/toastMessages';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import ReactTooltip from 'react-tooltip';
import startIcon from '../../../../assets/images/icons/plus-circle.svg';
import x from '../../../../assets/images/icons/x.svg';
import styles from './index.module.scss';

const themeColors = {
  sunYellow: '#FCD300',
  aquaGreen: '#06BF97',
  calmLilac: '#BEAAF3',
  warmOrange: '#FF8A4A',
  zingyGreen: '#8EEA36',
  skyBlue: '#019DF2',
  default: '#ECE7DC',
};

export const ActionCard: React.FunctionComponent<ImproveActionCardProps> = (props) => {
  const {
    actionData,
    className,
    title,
    description,
    dueDate,
    overdue,
    actionImage,
    theme,
    actionButtonName,
    handleRefetch,
    hideOptions,
    isDashboardAction,
  } = props;
  const { isDesktop } = useWindowDimensions();
  const history = useHistory();
  const [datePickerOpen, setDatePickerOpen] = useState<boolean>(false);
  const [selectedDueDate, setSelectedDueDate] = useState<any>(
    dayjs(new Date()).format('YYYY-MM-DD'),
  );
  const [markAsCompleted, setMarkAsCompleted] = useState<boolean>(false);
  const [dueDateUpdated, setDueDateUpdated] = useState<boolean>(false);
  const [removeModalOpen, setRemoveModalOpen] = useState<boolean>(false);

  const updateUserActionMutation = useUserActionsHooks.useUpdateUserAction();
  const removeUserActionMutation = useUserActionsHooks.useRemoveUserAction();

  useEffect(() => {
    if (dueDateUpdated) {
      handleUpdateAction(
        {
          id: actionData.id,
          actionType: actionData.actionType,
          dueDate: selectedDueDate,
          done: null,
          removed: null,
          start: null,
        },
        'Due date updated successfully',
      );
      setDatePickerOpen(false);
      setDueDateUpdated(false);
    }
  }, [dueDateUpdated, selectedDueDate]);

  const imageUrl = actionImage
    ? `../../../images/improve-actions/${actionImage.toLowerCase()}-${
        isDesktop ? 'desktop' : 'mobile'
      }.png`
    : `../../../images/improve-actions/default-${isDesktop ? 'desktop' : 'mobile'}.png`;

  const handleDatePickerChange = (date: string | null) => {
    if (date) {
      setSelectedDueDate(date);
    }
  };

  const handleMarkCompleted = async (e: { target: { checked: boolean } }) => {
    await handleUpdateAction(
      {
        id: actionData.id,
        actionType: actionData.actionType,
        dueDate: null,
        done: e.target.checked,
        removed: null,
        start: null,
      },
      'Action updated',
    );
    setMarkAsCompleted(e.target.checked);
    handleRefetch();
  };

  const handleUpdateAction = async (
    data: {
      id: number;
      actionType: string;
      dueDate: string | null;
      done: boolean | null;
      removed: boolean | null;
      start: boolean | null;
    },
    message?: string,
  ) => {
    try {
      await updateUserActionMutation.mutateAsync(
        {
          actionId: data.id,
          payload: {
            completed: data.done,
            dueDate: data.dueDate,
            started: data.start,
          },
        },
        {
          onSuccess: (dt) => {
            if (dt?.success) {
              showToast(
                message || ToastMessages.action.success.ACTION_UPDATED,
                undefined,
                'success',
              );
            } else {
              showToast(ToastMessages.common.TRY_AGAIN, undefined, 'error');
            }
          },
        },
      );
    } catch (err) {
      if (err instanceof Error) {
        showToast(ToastMessages.common.TRY_AGAIN, undefined, 'error');
      } else {
        showToast(ToastMessages.common.TRY_AGAIN, undefined, 'error');
      }
    }
  };

  const handleRemoveAction = async (actionId: number) => {
    await removeUserActionMutation.mutateAsync(actionId, {
      onSuccess: (dt) => {
        if (dt?.success) {
          showToast(ToastMessages.action.success.ACTION_REMOVED, undefined, 'success');
          handleRefetch();
        } else {
          showToast(ToastMessages.common.TRY_AGAIN, undefined, 'error');
        }
      },
    });
  };

  return (
    <>
      <Card className={`${className} ${hideOptions && styles.noOptions}`}>
        <div className={`${styles.container} ${isDashboardAction ? styles.dashboardAction : ''}`}>
          <div
            className={styles.cardActionImageCont}
            style={{
              backgroundImage: `url(${imageUrl})`,
            }}
          />

          <div className={styles.cardContentDesktop}>
            <div className={styles.cardHeaderSection}>
              <h3 className={styles.cardTitle}>{title}</h3>
              {!actionData?.completed && !hideOptions && !isDashboardAction && (
                <span onClick={() => setRemoveModalOpen(true)}>
                  remove
                  <i className="icon icon-x" />
                </span>
              )}
            </div>

            <div
              className={`${styles.cardMiddleContainer} ${
                isDashboardAction ? styles.dashboardAction : ''
              }`}
            >
              <div className={isDashboardAction ? styles.descriptionContainer : ''}>
                <p
                  className={`${styles.description} ${
                    isDashboardAction
                      ? actionButtonName !== null
                        ? styles.action
                        : styles.noAction
                      : ''
                  }`}
                >
                  {description}
                </p>
              </div>

              <div
                className={`${styles.btnContainer} ${
                  isDashboardAction
                    ? actionButtonName !== null
                      ? styles.action
                      : styles.noAction
                    : ''
                }`}
              >
                {actionData?.actionButtonTitle !== null && (
                  <SystemButton
                    type="button"
                    size="small"
                    className={`${styles.hsPrimaryBtn} ${hideOptions && styles.noOptions}`}
                    onClick={() => {
                      if (
                        actionData?.startedAt === null &&
                        !hideOptions &&
                        actionData.actionType !== 'USER_ACTION'
                      ) {
                        handleUpdateAction(
                          {
                            id: actionData.id,
                            actionType: actionData.actionType,
                            dueDate: null,
                            done: null,
                            removed: null,
                            start: true,
                          },
                          'Action started',
                        );
                      }

                      if (actionData.url) {
                        window.location.href = actionData.url;
                      }
                    }}
                  >
                    <span>{actionButtonName || 'Start now'}</span>
                    <Img src={startIcon} alt="HS Start Icon" className={styles.hsStartIcon} />
                  </SystemButton>
                )}
                {!isDashboardAction && (
                  <div className={`${styles.doneBtn} ${hideOptions && styles.noOptions}`}>
                    <Checkbox
                      id={actionData?.title || ''}
                      text={actionData.completed ? 'Completed' : 'Mark as complete'}
                      color={themeColors[theme as keyof typeof themeColors] || '#BEAAF3'}
                      onChange={handleMarkCompleted}
                      checked={actionData.completed ? actionData.completed : markAsCompleted}
                      disabled={
                        actionData.actionType !== 'USER_ACTION' &&
                        actionData.startedAt === null &&
                        hideOptions
                      }
                      minWidthEnabled
                    />
                  </div>
                )}
                {isDashboardAction && (
                  <span
                    onClick={() => setRemoveModalOpen(true)}
                    style={{ fontSize: '18px', marginTop: '5px', cursor: 'pointer' }}
                  >
                    <i className="icon icon-x" />
                  </span>
                )}
              </div>
            </div>

            {!hideOptions ? (
              <div
                className={`${styles.tooltipCont} ${actionData?.completed && styles.disabled}`}
                onClick={() => {
                  if (!actionData?.completed) setDatePickerOpen(true);
                }}
              >
                <div
                  className={styles.footerTooltipCont}
                  id={`footerTooltipCont ${actionData?.id}`}
                  data-tip
                  data-for={`footerTooltipCont ${actionData?.id}`}
                >
                  <div className={styles.cardFooterSection}>
                    {overdue !== null && actionData.dueDate ? (
                      <div className={overdue ? styles.dueSectionError : styles.dueSectionSuccess}>
                        <CalendarTodayIcon className={styles.calendar} aria-hidden="true" />
                        &nbsp;&nbsp;
                        {dueDate}
                      </div>
                    ) : (
                      <div className={styles.setDueDate}>
                        <CalendarTodayIcon className={styles.calendar} aria-hidden="true" />
                        &nbsp;&nbsp; Set due date
                      </div>
                    )}

                    {/* <div className={styles.calendarBtn}>
                      <Img src={CalendarIcon} alt="Calendar Icon" className={styles.calendar} />
                    </div> */}
                  </div>
                </div>
                <ReactTooltip id={`footerTooltipCont ${actionData?.id}`}>
                  <span>Set due date</span>
                </ReactTooltip>
              </div>
            ) : (
              <div>&nbsp;</div>
            )}
          </div>
        </div>

        <div className={styles.cardContentMobile}>
          <div
            className={styles.cardActionImageMobile}
            style={{
              backgroundImage: `url(${imageUrl})`,
            }}
          />

          <div className={`${styles.mobileCardContentContainer}`}>
            <div className={styles.mobileMiddleContainer}>
              <h3 className={styles.mobileCardTitle}>{title}</h3>
              <p className={styles.mobileCardDescription}>{description}</p>

              <div className={styles.mobileButtonContainer}>
                {actionData.actionButtonTitle !== null && (
                  <DefaultButton
                    type="button"
                    className={`${styles.hsPrimaryBtn} ${styles.fullWidth}`}
                    variant="primary"
                    path={actionData?.url !== null ? actionData?.url : null || undefined}
                    onClick={async () => {
                      if (
                        actionData.startedAt === null &&
                        actionData.actionType !== 'USER_ACTION'
                      ) {
                        await handleUpdateAction(
                          {
                            id: actionData.id,
                            actionType: actionData.actionType,
                            dueDate: null,
                            done: null,
                            removed: null,
                            start: true,
                          },
                          'Action started',
                        );
                      }

                      if (actionData.url) {
                        window.location.href = actionData.url;
                      }
                    }}
                  >
                    <span>{actionButtonName || 'Start now'}</span>
                    <Img src={startIcon} alt="HS Start Icon" className={styles.hsStartIcon} />
                  </DefaultButton>
                )}

                {!isDashboardAction && (
                  <div className={styles.doneBtn}>
                    <Checkbox
                      id={actionData?.title || ''}
                      text={actionData.completed ? 'Completed' : 'Mark as complete'}
                      color={themeColors[theme as keyof typeof themeColors] || '#BEAAF3'}
                      onChange={handleMarkCompleted}
                      checked={actionData.completed ? actionData.completed : markAsCompleted}
                      disabled={
                        actionData.actionType !== 'USER_ACTION' && actionData.startedAt === null
                      }
                    />
                  </div>
                )}

                <div className={styles.mobileCardFooter}>
                  <div
                    className={styles.mobileDueSection}
                    onClick={() => {
                      if (!actionData?.completed) setDatePickerOpen(true);
                    }}
                  >
                    {overdue !== null && actionData.dueDate ? (
                      <div className={overdue ? styles.dueSectionError : styles.dueSectionSuccess}>
                        <CalendarTodayIcon className={styles.calendar} aria-hidden="true" />
                        &nbsp;&nbsp;{dueDate}
                      </div>
                    ) : (
                      <div className={styles.setDueDate}>
                        <CalendarTodayIcon className={styles.calendar} aria-hidden="true" />
                        &nbsp;&nbsp;Set due date
                      </div>
                    )}

                    {/* <div className={styles.calendarBtn}>
                      <Img src={CalendarIcon} alt="Calendar Icon" className={styles.calendar} />
                    </div> */}
                  </div>

                  {!actionData?.completed && (
                    <div className={styles.removeMobile}>
                      <span onClick={() => setRemoveModalOpen(true)}>remove</span>
                      <CloseIcon className={styles.removeIcon} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {removeModalOpen && (
        <Modal
          open={removeModalOpen}
          setOpen={setRemoveModalOpen}
          overlay
          onTapBackgroundClose
          className={styles.removeModal}
        >
          <div className={styles.removeModalContent}>
            <div className={styles.icons} onClick={() => setRemoveModalOpen(false)}>
              <div className={styles.close}>Close</div>
              <img src={x} alt="close icon" />
            </div>
            <h4>Are you sure you want to remove this action?</h4>
            <p>Action will be removed from your plan permanently. Please confirm.</p>

            <div className={styles.btnContainer}>
              <DefaultButton
                className={styles.hsSecondaryBtn}
                onClick={() => setRemoveModalOpen(false)}
              >
                No
              </DefaultButton>
              <DefaultButton
                className={`${styles.hsPrimaryBtn} ${styles.discardFullWidth}`}
                onClick={() => {
                  handleRemoveAction(actionData.id);
                  setRemoveModalOpen(false);
                }}
              >
                Yes
              </DefaultButton>
            </div>
          </div>
        </Modal>
      )}

      {datePickerOpen && (
        <Modal
          open={datePickerOpen}
          setOpen={setDatePickerOpen}
          overlay
          onTapBackgroundClose
          className={styles.datePickerModal}
        >
          <StaticDatePickerX
            selectedDate={selectedDueDate}
            handleChange={handleDatePickerChange}
            onClose={() => setDatePickerOpen(false)}
            onAccept={() => {
              setDueDateUpdated(true);
            }}
          />
        </Modal>
      )}
    </>
  );
};

ActionCard.defaultProps = {
  className: styles.card,
  overdue: false,
  completed: false,
  actionImage: null,
  theme: '',
  hideOptions: false,
  isDashboardAction: false,
};

type ImproveActionCardProps = {
  actionData: UserAction;
  className?: string;
  title: string;
  description: string;
  dueDate: string;
  overdue?: boolean | null;
  completed?: boolean;
  actionImage?: string | null;
  theme?: string | null;
  actionButtonName: string | null;
  handleRefetch: () => void;
  hideOptions?: boolean;
  isDashboardAction?: boolean;
};
